import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, ListGroup, ListGroupItem, Badge, Button, Input } from "reactstrap";
import moment from "moment";

function PresenceModal({
  isOpen,
  onClose,
  timeline = [],
  totals = { totalRecords: 0, present: 0, absent: 0, usersWithPresence: 0, usersWithoutPresence: 0 },
  presenceUsers = [],
  usersCompany = [],
  onOpenHistory,
  onExport,
  formatTimelineLabel
}) {
  const [searchValue, setSearchValue] = React.useState("");
  const participantsTotal = (totals.usersWithPresence || 0) + (totals.usersWithoutPresence || 0);
  const attendanceBase = (totals.present || 0) + (totals.absent || 0);
  const attendanceRate = attendanceBase > 0 ? Math.round((totals.present / attendanceBase) * 100) : 0;
  const engagementRate = participantsTotal > 0 ? Math.round((totals.usersWithPresence / participantsTotal) * 100) : 0;

  const matchesPresenceSearch = user => {
    const term = (searchValue || "").trim().toLowerCase();
    if (!term) return true;
    const decoratedUser = usersCompany.find(u => String(u.id) === String(user.id)) || {};
    const fields = [
      user.name,
      user.lastName,
      user.email,
      decoratedUser.name,
      decoratedUser.lastName,
      decoratedUser.email
    ];
    return fields
      .filter(Boolean)
      .map(value => value.toString().toLowerCase())
      .some(value => value.includes(term));
  };

  const filteredUsers = Array.isArray(presenceUsers) ? presenceUsers.filter(matchesPresenceSearch) : [];
  const exportableUsersCount = filteredUsers.length;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Presenças · visão completa</ModalHeader>
      <ModalBody>
        <Row className="mb-4 text-center">
          <Col md="3" sm="6" className="mb-3">
            <div className="text-sm text-muted">Registros totais</div>
            <div className="h3 font-weight-bold mb-0">{totals.totalRecords}</div>
          </Col>
          <Col md="3" sm="6" className="mb-3">
            <div className="text-sm text-muted">Presenças</div>
            <div className="h3 font-weight-bold mb-0 text-success">{totals.present}</div>
          </Col>
          <Col md="3" sm="6" className="mb-3">
            <div className="text-sm text-muted">Faltas</div>
            <div className="h3 font-weight-bold mb-0 text-danger">{totals.absent}</div>
          </Col>
          <Col md="3" sm="6" className="mb-3">
            <div className="text-sm text-muted">Usuários monitorados</div>
            <div className="h3 font-weight-bold mb-0">{participantsTotal}</div>
          </Col>
        </Row>
        <Row className="mb-4">
          <Col md="6" className="mb-3">
            <div className="text-sm text-muted mb-2">Taxa de presença</div>
            <div className="d-flex align-items-center">
              <div className="progress flex-grow-1" style={{ height: "6px" }}>
                <div className="progress-bar bg-success" role="progressbar" style={{ width: `${attendanceRate}%` }} aria-valuenow={attendanceRate} aria-valuemin="0" aria-valuemax="100"></div>
              </div>
              <span className="ml-3 text-sm font-weight-bold">{attendanceRate}%</span>
            </div>
          </Col>
          <Col md="6" className="mb-3">
            <div className="text-sm text-muted mb-2">Cobertura de presença</div>
            <div className="d-flex align-items-center">
              <div className="progress flex-grow-1" style={{ height: "6px" }}>
                <div className="progress-bar bg-info" role="progressbar" style={{ width: `${engagementRate}%` }} aria-valuenow={engagementRate} aria-valuemin="0" aria-valuemax="100"></div>
              </div>
              <span className="ml-3 text-sm font-weight-bold">{engagementRate}%</span>
            </div>
          </Col>
        </Row>

        <h6 className="text-uppercase text-muted ls-1 mt-2 mb-2">Histórico completo</h6>
        <ListGroup flush>
          {Array.isArray(timeline) && timeline.length > 0 ? (
            timeline.map((item, index) => {
              const present = Number(item?.present ?? item?.presences ?? 0) || 0;
              const absent = Number(item?.absent ?? item?.absences ?? 0) || 0;
              const totalRecords = Number(item?.totalRecords ?? item?.total ?? item?.count ?? present + absent) || 0;
              const label = typeof formatTimelineLabel === 'function' ? formatTimelineLabel(item) : (item.label || "Período");
              return (
                <ListGroupItem key={`${label}-${index}`} className="d-flex justify-content-between align-items-center py-2">
                  <div>
                    <div className="font-weight-bold">{label}</div>
                    <div className="small text-muted">{totalRecords} registros</div>
                  </div>
                  <div className="text-sm text-right">
                    <span className="text-success mr-3"><i className="fas fa-check mr-1"></i>{present} pres.</span>
                    <span className="text-danger"><i className="fas fa-times mr-1"></i>{absent} falt.</span>
                  </div>
                </ListGroupItem>
              );
            })
          ) : (
            <ListGroupItem className="py-3 d-flex justify-content-between align-items-center">
              <div>
                <div className="font-weight-bold">Resumo consolidado</div>
                <div className="small text-muted">Todos os períodos registrados</div>
              </div>
              <div className="text-sm text-right">
                <span className="text-success mr-3"><i className="fas fa-check mr-1"></i>{totals.present} pres.</span>
                <span className="text-danger"><i className="fas fa-times mr-1"></i>{totals.absent} falt.</span>
              </div>
            </ListGroupItem>
          )}
        </ListGroup>

        {Array.isArray(presenceUsers) && presenceUsers.length > 0 ? (
          <div className="mb-4">
            <Input
              type="search"
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder="Pesquisar alunos por nome ou e-mail"
            />
          </div>
        ) : null}

        <h6 className="text-uppercase text-muted ls-1 mt-4 mb-2">Alunos</h6>
        {filteredUsers.length > 0 ? (
          <ListGroup flush className="mb-3">
            {filteredUsers.map(user => {
              const decoratedUser = usersCompany.find(u => String(u.id) === String(user.id)) || {};
              const avatar = decoratedUser.photo || user.photo || "/images/defaultAvatar.png";
              const email = user.email || decoratedUser.email || "Sem e-mail";
              const nameParts = [user.name || decoratedUser.name, user.lastName || decoratedUser.lastName].filter(Boolean);
              const displayName = nameParts.length > 0 ? nameParts.join(" ") : "Usuário";
              const lastStatus = (user.lastStatus || "").toLowerCase();
              const statusLabel = lastStatus === "falta" ? "Último registro: Falta" : (lastStatus === "presente" ? "Último registro: Presente" : "Último registro desconhecido");
              const statusColor = lastStatus === "falta" ? "danger" : "success";
              const detailData = {
                ...decoratedUser,
                id: decoratedUser.id || user.id,
                name: decoratedUser.name || user.name,
                lastName: decoratedUser.lastName || user.lastName,
                email,
                photo: avatar,
                presenceDetail: {
                  presences: user.presences,
                  absences: user.absences,
                  lastDate: user.lastDate
                }
              };
              const formattedDate = user.lastDate ? moment(user.lastDate).format("DD/MM/YYYY") : null;
              return (
                <ListGroupItem key={`presence-${user.id}`} className="py-2 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="mr-3" style={{ width: "40px", height: "40px", borderRadius: "50%", overflow: "hidden" }}>
                      <img src={avatar} alt={displayName} className="rounded-circle" style={{ width: "40px", height: "40px", objectFit: "cover" }} />
                    </div>
                    <div>
                      <div className="font-weight-bold d-flex align-items-center">
                        <span className="mr-2">{displayName}</span>
                        <Badge color={statusColor} pill>{statusLabel}</Badge>
                      </div>
                      <div className="small text-muted">{email}{formattedDate ? ` • ${formattedDate}` : ""}</div>
                      <div className="small text-muted">{user.presences} presenças · {user.absences} faltas</div>
                    </div>
                  </div>
                  <Button size="sm" color="link" onClick={() => onOpenHistory && onOpenHistory(detailData)}>Ver</Button>
                </ListGroupItem>
              );
            })}
          </ListGroup>
        ) : (
          <div className="text-center text-muted py-3">{(searchValue || "").trim() ? "Nenhum aluno corresponde à pesquisa." : "Nenhum aluno com presença registrada no período."}</div>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <Button
          color="primary"
          onClick={() => {
            if (!onExport) return;
            const rows = filteredUsers.map(user => {
              const decoratedUser = usersCompany.find(u => String(u.id) === String(user.id)) || {};
              const id = decoratedUser.id || user.id || "";
              const name = decoratedUser.name || user.name || "";
              const lastName = decoratedUser.lastName || user.lastName || "";
              const fullName = [name, lastName].filter(Boolean).join(" ");
              const email = user.email || decoratedUser.email || "";
              const role = user.roleLabel || decoratedUser.roleLabel || "";
              const presences = user.presences !== undefined ? user.presences : (user.presenceCount ?? user.present ?? 0);
              const absences = user.absences !== undefined ? user.absences : (user.absenceCount ?? user.absent ?? 0);
              const lastDate = user.lastDate || user.last_activity || "";
              const lastDateFormatted = lastDate ? moment(lastDate).format("DD/MM/YYYY") : "";
              const lastStatus = user.lastStatus || "";
              const blocked = (user.isBlocked !== undefined ? user.isBlocked : decoratedUser.isBlocked) ? "Sim" : "Não";

              return {
                id,
                name,
                lastName,
                fullName,
                email,
                role,
                presences,
                absences,
                lastDate,
                lastDateFormatted,
                lastStatus,
                blocked
              };
            });

            onExport(rows);
          }}
          disabled={exportableUsersCount === 0}
        >
          Exportar CSV
        </Button>
        <Button color="secondary" onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default PresenceModal;
